import { Component, computed, effect, inject } from '@angular/core';
import { PersonMeasuredValuesStore } from './person-measured-values.store';
import { FilterBuilder } from '../../../helper/filter.builder';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { WorkDataVisitObject } from '@libs/types/src/services/object-type/work/work-visit.object';
import { WorkDataInterventionObject } from '@libs/types/src/services/object-type/work/work-intervention.object';
import { WorkDataDoctorRecordObject } from '@libs/types/src/services/object-type/work/work-doctor-record.object';
import { SessionService } from '../../../../../services/session.service';
import { localDate } from '../../../../shared/pipes/local-date.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { UIChart } from '@openng/optimus-ui/chart';

@Component({
    selector: 'app-person-measured-values',
    templateUrl: './person-measured-values.component.html',
    styleUrls: ['./person-measured-values.component.scss'],
    providers: [PersonMeasuredValuesStore],
    imports: [
        TranslatePipe,
        UIChart,
    ],
})
export class PersonMeasuredValuesComponent {
    protected readonly store = inject(PersonMeasuredValuesStore);
    private readonly session = inject(SessionService);

    weightGraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('weight'),
                borderColor: '#00ff55',
            },
        ],
    }));
    pressureGraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('systolicPressure'),
                borderColor: '#ff0000',
            },
            {
                data: this.generateValues('diastolicPressure'),
                borderColor: '#0000ff',
            },
        ],
    }));
    glycemiaGraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('glycemia'),
                borderColor: '#ff00ff',
            },
        ],
    }));
    painGraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('pain'),
                borderColor: '#ff0099',
            },
        ],
    }));
    pulseGraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('pulse'),
                borderColor: '#00ffff',
            },
        ],
    }));
    breathGraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('breath'),
                borderColor: '#ff7700',
            },
        ],
    }));
    spo2GraphData = computed(() => ({
        labels: this.getDates(),
        datasets: [
            {
                data: this.generateValues('spo2'),
                borderColor: '#ff0077',
            },
        ],
    }));

    lineGraphOptions = {
        responsive: false,
        aspectRatio: 7,
        devicePixelRatio: 1,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                display: false,
            },
            y: {
                display: false,
            },
        },
        elements: {
            line: {
                borderWidth: 1,
            },
        },
    };

    constructor() {
        effect(() => {
            this.getMeasuredValues();
        });
    }

    async getMeasuredValues() {
        const folderId = this.store.folderId();
        if (!folderId) return;
        const filter = new FilterBuilder()
            .folderId(folderId)
            .where(
                'workType',
                [
                    WorkTypeEnum.INTERVENTION,
                    WorkTypeEnum.VISIT,
                    WorkTypeEnum.DOCTOR_RECORD,
                ],
                'IN',
            )
            .limit(100)
            .sort('dateFrom', 'ASC');
        const values = `
            systolicPressure
            diastolicPressure
            pulse
            breath
            glycemia
            spo2
            pain
            weight
        `;
        const frag = `
            id
            dateFrom
            workType
            workData {
                ... on WorkDataVisitObject {
                    ${values}
                }
                ... on WorkDataInterventionObject {
                    ${values}
                }
                ... on WorkDataDoctorRecordObject {
                    ${values}
                }
            }
        `;
        await this.store.api.getWorks(filter.create(), frag);
    }

    protected generateValues(field: 'weight' | 'systolicPressure' | 'diastolicPressure' | 'pulse' | 'breath' | 'glycemia' | 'spo2' | 'pain') {
        return this.store.items()
            .map((item) =>
                (item?.workData as WorkDataVisitObject | WorkDataInterventionObject | WorkDataDoctorRecordObject)?.[field]);
    }

    private getDates() {
        return this.store.items().map((item) => localDate(item.dateFrom, this.session));
    }
}
